import { createFileRoute } from '@tanstack/react-router'
import { useEvent, useReporting, useEventInsightsStats, useOrderReviews, useOrderReviewSummary, useItemReviews, useItemReviewSummary, useVendorEvents, queryKeys } from '@/lib/api/queries'
import { DateFilter } from '@/components/ui/date-filter'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive } from '@/lib/eventTime'
import { useState, useMemo, useEffect } from 'react'
import { useQueries } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Package, ShoppingCart, Users, Activity, BarChart2, Coins, DollarSign, ChevronUp, ChevronDown, TrendingUp, Store, Wallet, MapPin, Star, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts'
import { useModal } from '@/components/modals/modal-container'
import type { OrderReview, OrderReviewSummary, User } from '@/lib/api/types'
import { usePermission, useAllPermissions } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useAuth } from '@/lib/auth'
import { apiClient } from '@/lib/api/client'
import { ExportDropdown, PDF_FORMAT } from '@/components/export-dropdown'
import { format } from 'date-fns'

export const Route = createFileRoute('/_authenticated/events/$eventId/reporting')({
  component: ReportingPage,
})

type MainTabType = 'overview' | 'insights' | 'reviews'
type TabType = 'vendor' | 'item' | 'date'
type SortField = 'name' | 'orders' | 'items_sold' | 'customers' | 'tokens_collected' | 'revenue' | 'date'

// Get tab from URL hash
function getTabFromHash(): MainTabType {
  if (typeof window === 'undefined') return 'overview'
  const hash = window.location.hash.replace('#', '')
  if (hash === 'insights') return 'insights'
  if (hash === 'reviews') return 'reviews'
  return 'overview'
}

interface StatCardProps {
  icon: React.ReactNode
  iconBg: string
  value: string | number
  label: string
}

function StatCard({ icon, iconBg, value, label }: StatCardProps) {
  return (
    <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 pt-4 md:p-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-md ${iconBg} mr-3`}>{icon}</div>
        <div>
          <p className="text-sm sm:text-xl font-bold text-neutral-900 dark:text-neutral-100">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

// Format number with k suffix
function formatNumber(num: number): string {
  if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'k'
  }
  return num.toLocaleString()
}

// Format currency
function formatCurrency(num: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num)
}

// Sort icon component
function SortIcon({ active, ascending }: { active: boolean; ascending: boolean }) {
  if (!active) return null
  return ascending ? (
    <ChevronUp className="w-3 h-3" />
  ) : (
    <ChevronDown className="w-3 h-3" />
  )
}

// Chart colors
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

function ReportingPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const { token } = useAuth()

  // Permission check
  const canViewReporting = usePermission('can_view_reporting')
  const canExportInsights = useAllPermissions(['can_export_reports', 'can_view_reporting'])
  
  const [mainTab, setMainTab] = useState<MainTabType>(getTabFromHash)
  const [activeTab, setActiveTab] = useState<TabType>('vendor')
  const [sortField, setSortField] = useState<SortField>('tokens_collected')
  const [sortAscending, setSortAscending] = useState(false)
  
  // Date picker state
  const { data: event } = useEvent(eventIdNum)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  
  // Insights endpoint filters by date-only start_date/end_date. Use the
  // event-local CALENDAR date the user picked (slice), not a server-tz
  // conversion: converting the 23:59 end-of-day through the server tz rolls
  // the date forward a day for events west of the server, widening results.
  const dateParams = useMemo(() => {
    if (!startDate && !endDate) return undefined
    const params: { start_date?: string; end_date?: string } = {}
    if (startDate) params.start_date = startDate.slice(0, 10)
    if (endDate) params.end_date = endDate.slice(0, 10)
    return params
  }, [startDate, endDate])

  // Overview endpoint (useReporting) filters by full-datetime created_at
  // gte/lte, so the same range drives the overview cards/chart. Full-datetime
  // conversion is exact (no date rollover) — only date-only truncation rolls.
  const reportingDateParams = useMemo(() => {
    const tz = event?.timezone || DEFAULT_EVENT_TIMEZONE
    const params: { created_at__gte?: string; created_at__lte?: string } = {}
    if (startDate) params.created_at__gte = eventLocalToServerNaive(startDate, tz)
    if (endDate) params.created_at__lte = eventLocalToServerNaive(endDate, tz)
    return params
  }, [startDate, endDate, event?.timezone])
  
  
  // Sync tab with URL hash
  useEffect(() => {
    const handleHashChange = () => {
      setMainTab(getTabFromHash())
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])
  
  // Update URL hash when tab changes
  const handleMainTabChange = (tab: MainTabType) => {
    setMainTab(tab)
    window.history.replaceState(null, '', `#${tab}`)
  }
  
  const { data: reportingData, isLoading, error } = useReporting(eventIdNum, reportingDateParams)
  const { data: insightsData, isLoading: insightsLoading } = useEventInsightsStats(eventIdNum, { top_count: 10, ...dateParams })
  
  // Extract and compute stats
  const aggregates = reportingData?.aggregates || {}
  const orders = aggregates.orders_received || 0
  const itemsSold = aggregates.items_sold || 0
  const customers = aggregates.customers || 0
  const tokensRevenue = aggregates.tokens || 0
  const avgOrder = aggregates.avg || 0
  const avgTokenSpend = orders > 0 ? Math.round((tokensRevenue / orders) * 10) / 10 : 0

  // Process vendors for table
  const vendors = useMemo(() => {
    const vendorsData = reportingData?.vendors || []
    return vendorsData.map(v => ({
      name: v.name || '',
      location: v.location || '',
      orders: v.orders_received || 0,
      items_sold: v.items_sold || 0,
      customers: v.customers || 0,
      tokens_collected: v.revenue || 0,
    }))
  }, [reportingData?.vendors])

  // Process items for table - include customers
  // API returns nested field names like vendor_event_item__name
  const items = useMemo(() => {
    const itemsData = reportingData?.items || []
    return itemsData.map(i => ({
      name: i.vendor_event_item__name || i.name || '',
      vendor_name: i.vendor_event_item__vendor_event__vendor__name || i.vendor_name || '',
      orders: i.orders_received || i.orders || 0,
      items_sold: i.items_sold || 0,
      customers: i.customers || 0,
      tokens_collected: i.revenue || 0,
    }))
  }, [reportingData?.items])

  // Process date data for table
  // API returns orders_received, not orders
  const dateData = useMemo(() => {
    const dates = reportingData?.date || []
    return dates.map(d => {
      // Format date for display
      let formattedDate = d.created_at__date || ''
      try {
        const parsedDate = new Date(formattedDate)
        if (!isNaN(parsedDate.getTime())) {
          formattedDate = parsedDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
          })
        }
      } catch {
        // Keep original format if parsing fails
      }
      
      return {
        date: d.created_at__date || '',
        formatted_date: formattedDate,
        orders: d.orders_received || d.orders || 0,
        items_sold: d.items_sold || 0,
        customers: d.customers || 0,
        tokens_collected: d.revenue || 0,
      }
    })
  }, [reportingData?.date])

  // Chart data
  const chartData = useMemo(() => {
    const dates = reportingData?.date || []
    return [...dates].reverse().map(d => ({
      date: d.created_at__date || '',
      value: d.revenue || 0,
    }))
  }, [reportingData?.date])

  // Sort function
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAscending(!sortAscending)
    } else {
      setSortField(field)
      setSortAscending(false) // Default to descending for new sort
    }
  }

  // Get sorted data based on active tab
  const sortedVendors = useMemo(() => {
    return [...vendors].sort((a, b) => {
      const aVal = a[sortField as keyof typeof a] ?? 0
      const bVal = b[sortField as keyof typeof b] ?? 0
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortAscending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortAscending ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal)
    })
  }, [vendors, sortField, sortAscending])

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aVal = a[sortField as keyof typeof a] ?? 0
      const bVal = b[sortField as keyof typeof b] ?? 0
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortAscending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortAscending ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal)
    })
  }, [items, sortField, sortAscending])

  const sortedDates = useMemo(() => {
    return [...dateData].sort((a, b) => {
      if (sortField === 'date') {
        return sortAscending ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)
      }
      // For numeric fields
      const numericFields: (keyof typeof a)[] = ['orders', 'items_sold', 'customers', 'tokens_collected']
      if (numericFields.includes(sortField as keyof typeof a)) {
        const aVal = a[sortField as keyof typeof a] ?? 0
        const bVal = b[sortField as keyof typeof b] ?? 0
        return sortAscending ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal)
      }
      return 0
    })
  }, [dateData, sortField, sortAscending])

  // Get token value from event (default 2.0)
  const tokenValue = event?.token_value || 2.0

  // Permission guard — after ALL hooks so hook order stays stable across
  // renders (rules-of-hooks). The memos above are null-safe on undefined data.
  if (!canViewReporting) {
    return <AccessDenied message="You don't have permission to view reporting." />
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Header with Date Range */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 w-full">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Reporting</h1>
          <div className="flex items-center gap-2 mt-3 sm:mt-0">
            {canExportInsights && (
              <ExportDropdown
                formats={[PDF_FORMAT]}
                onExport={async () => {
                  if (!token) throw new Error('Not authenticated')
                  await apiClient.exportInsights(token, eventIdNum)
                }}
              />
            )}
          <DateFilter
            value={{ gte: startDate || undefined, lte: endDate || undefined }}
            onChange={(v) => { setStartDate(v.gte ?? ''); setEndDate(v.lte ?? '') }}
            eventTimeZone={event?.timezone}
            defaultMonth={event?.start_date_time ? new Date(event.start_date_time) : undefined}
          />
          </div>
        </div>

        {/* Main Tab Navigation */}
        <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex space-x-6">
            <button
              onClick={() => handleMainTabChange('overview')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                mainTab === 'overview'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              Overview
            </button>
            <button
              onClick={() => handleMainTabChange('insights')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                mainTab === 'insights'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              Insights
            </button>
            <button
              onClick={() => handleMainTabChange('reviews')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                mainTab === 'reviews'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              Reviews
            </button>
          </div>
        </div>

        {mainTab === 'overview' ? (
          <OverviewTab
            isLoading={isLoading}
            error={error}
            orders={orders}
            itemsSold={itemsSold}
            customers={customers}
            tokensRevenue={tokensRevenue}
            avgOrder={avgOrder}
            avgTokenSpend={avgTokenSpend}
            chartData={chartData}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            sortField={sortField}
            sortAscending={sortAscending}
            handleSort={handleSort}
            setSortField={setSortField}
            setSortAscending={setSortAscending}
            sortedVendors={sortedVendors}
            sortedItems={sortedItems}
            sortedDates={sortedDates}
            tokenValue={tokenValue}
          />
        ) : mainTab === 'insights' ? (
          <InsightsTab
            isLoading={insightsLoading}
            data={insightsData}
          />
        ) : (
          <ReviewsTab eventId={eventIdNum} />
        )}
      </div>
    </div>
  )
}

// Overview Tab Component
function OverviewTab({
  isLoading,
  error,
  orders,
  itemsSold,
  customers,
  tokensRevenue,
  avgOrder,
  avgTokenSpend,
  chartData,
  activeTab,
  setActiveTab,
  sortField,
  sortAscending,
  handleSort,
  setSortField,
  setSortAscending,
  sortedVendors,
  sortedItems,
  sortedDates,
  tokenValue,
}: {
  isLoading: boolean
  error: Error | null
  orders: number
  itemsSold: number
  customers: number
  tokensRevenue: number
  avgOrder: number
  avgTokenSpend: number
  chartData: Array<{ date: string; value: number }>
  activeTab: TabType
  setActiveTab: (tab: TabType) => void
  sortField: SortField
  sortAscending: boolean
  handleSort: (field: SortField) => void
  setSortField: (field: SortField) => void
  setSortAscending: (ascending: boolean) => void
  sortedVendors: Array<{ name: string; location: string; orders: number; items_sold: number; customers: number; tokens_collected: number }>
  sortedItems: Array<{ name: string; vendor_name: string; orders: number; items_sold: number; customers: number; tokens_collected: number }>
  sortedDates: Array<{ date: string; formatted_date: string; orders: number; items_sold: number; customers: number; tokens_collected: number }>
  tokenValue: number
}) {
  return (
    <>
      {/* Stat Cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 w-full animate-pulse">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 pt-4 md:p-4"
            >
              <div className="flex items-center">
                <div className="p-2 rounded-md bg-neutral-100 dark:bg-neutral-800 mr-3 w-9 h-9" />
                <div>
                  <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-16 mb-2" />
                  <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="mb-10 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-400">Failed to load reporting data: {error.message}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 w-full">
          <StatCard
            icon={<Package className="w-5 h-5 text-blue-500" />}
            iconBg="bg-blue-50 dark:bg-blue-900/20"
            value={orders}
            label="Orders"
          />
          <StatCard
            icon={<ShoppingCart className="w-5 h-5 text-green-500" />}
            iconBg="bg-green-50 dark:bg-green-900/20"
            value={itemsSold}
            label="Items Sold"
          />
          <StatCard
            icon={<Users className="w-5 h-5 text-yellow-500" />}
            iconBg="bg-yellow-50 dark:bg-yellow-900/20"
            value={customers}
            label="Customers"
          />
          <StatCard
            icon={<Activity className="w-5 h-5 text-cyan-500" />}
            iconBg="bg-cyan-50 dark:bg-cyan-900/20"
            value={avgTokenSpend}
            label="Tokens/Order"
          />
          <StatCard
            icon={<BarChart2 className="w-5 h-5 text-orange-500" />}
            iconBg="bg-orange-50 dark:bg-orange-900/20"
            value={avgOrder}
            label="Avg. Price"
          />
          <StatCard
            icon={<Coins className="w-5 h-5 text-purple-500" />}
            iconBg="bg-purple-50 dark:bg-purple-900/20"
            value={tokensRevenue}
            label="Tokens Issued"
          />
        </div>
      )}

      {/* Sales Over Time Chart */}
      <div className="mb-8 w-full">
        <div className="w-full bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
          <div className="px-6 pt-6">
            <h2 className="text-2xl font-medium text-neutral-900 dark:text-neutral-100 mb-2">Sales Over Time</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">Total sales revenue by day</p>
          </div>
          <div className="px-4 pb-6">
            {isLoading ? (
              <div className="h-[450px] w-full bg-neutral-50 dark:bg-neutral-900 rounded animate-pulse" />
            ) : chartData.length === 0 ? (
              <div className="h-[450px] w-full bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
                <p className="text-neutral-500 dark:text-neutral-400">No sales data available</p>
              </div>
            ) : (
              <div className="h-[450px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-800" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      className="text-neutral-500 dark:text-neutral-400"
                      tickLine={false}
                      axisLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      className="text-neutral-500 dark:text-neutral-400"
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value: number) => value.toLocaleString()}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--tooltip-bg, white)',
                        border: '1px solid var(--tooltip-border, #e5e5e5)',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                      labelStyle={{ fontWeight: 600 }}
                      formatter={(value) => [(value ?? 0).toLocaleString(), 'Sales']}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex space-x-6 overflow-x-auto">
          <button
            onClick={() => { setActiveTab('vendor'); setSortField('tokens_collected'); setSortAscending(false); }}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'vendor'
                ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            By Vendor
          </button>
          <button
            onClick={() => { setActiveTab('item'); setSortField('tokens_collected'); setSortAscending(false); }}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'item'
                ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            By Item
          </button>
          <button
            onClick={() => { setActiveTab('date'); setSortField('date'); setSortAscending(false); }}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'date'
                ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            By Date
          </button>
        </div>
      </div>

      {/* Reporting Table */}
      <div className="-mx-4 sm:mx-0">
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 sm:rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              {activeTab === 'vendor' && (
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>VENDOR</span>
                      <SortIcon active={sortField === 'name'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('orders')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>ORDERS</span>
                      <SortIcon active={sortField === 'orders'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('items_sold')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>ITEMS SOLD</span>
                      <SortIcon active={sortField === 'items_sold'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('customers')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>CUSTOMERS</span>
                      <SortIcon active={sortField === 'customers'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('tokens_collected')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>TOKENS</span>
                      <SortIcon active={sortField === 'tokens_collected'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('revenue')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>REVENUE</span>
                      <SortIcon active={sortField === 'revenue'} ascending={sortAscending} />
                    </button>
                  </th>
                </tr>
              )}
              {activeTab === 'item' && (
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>ITEM</span>
                      <SortIcon active={sortField === 'name'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    VENDOR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('orders')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>ORDERS</span>
                      <SortIcon active={sortField === 'orders'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('items_sold')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>ITEMS SOLD</span>
                      <SortIcon active={sortField === 'items_sold'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('customers')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>CUSTOMERS</span>
                      <SortIcon active={sortField === 'customers'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('tokens_collected')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>TOKENS</span>
                      <SortIcon active={sortField === 'tokens_collected'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('revenue')}
                      className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>REVENUE</span>
                      <SortIcon active={sortField === 'revenue'} ascending={sortAscending} />
                    </button>
                  </th>
                </tr>
              )}
              {activeTab === 'date' && (
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('date')}
                      className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>DATE</span>
                      <SortIcon active={sortField === 'date'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('orders')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>ORDERS</span>
                      <SortIcon active={sortField === 'orders'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('items_sold')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>ITEMS SOLD</span>
                      <SortIcon active={sortField === 'items_sold'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('customers')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>CUSTOMERS</span>
                      <SortIcon active={sortField === 'customers'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('tokens_collected')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>TOKENS COLLECTED</span>
                      <SortIcon active={sortField === 'tokens_collected'} ascending={sortAscending} />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('revenue')}
                      className="flex m-auto items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <span>REVENUE</span>
                      <SortIcon active={sortField === 'revenue'} ascending={sortAscending} />
                    </button>
                  </th>
                </tr>
              )}
            </thead>
            <tbody className="bg-white dark:bg-[#131313]">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-neutral-400 dark:text-neutral-500 text-sm">
                    Loading...
                  </td>
                </tr>
              ) : activeTab === 'vendor' ? (
                sortedVendors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-neutral-400 dark:text-neutral-500 text-sm">
                      No vendors found.
                    </td>
                  </tr>
                ) : (
                  sortedVendors.map((vendor, index) => (
                    <tr key={index} className="hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors duration-150">
                      <td className="px-6 py-4 text-sm font-medium text-neutral-900 dark:text-neutral-100 max-w-xs break-words">
                        {vendor.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {vendor.orders.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {vendor.items_sold.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {vendor.customers.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {vendor.tokens_collected.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        ${(vendor.tokens_collected * tokenValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))
                )
              ) : activeTab === 'item' ? (
                sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-neutral-400 dark:text-neutral-500 text-sm">
                      No items found.
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((item, index) => (
                    <tr key={index} className="hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors duration-150">
                      <td className="px-6 py-4 text-sm font-medium text-neutral-900 dark:text-neutral-100 max-w-xs break-words">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-700 dark:text-neutral-300 max-w-xs break-words">
                        {item.vendor_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {item.orders.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {item.items_sold.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {item.customers.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {item.tokens_collected.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        ${(item.tokens_collected * tokenValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))
                )
              ) : (
                sortedDates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-neutral-400 dark:text-neutral-500 text-sm">
                      No date data found.
                    </td>
                  </tr>
                ) : (
                  sortedDates.map((dateRow, index) => (
                    <tr key={index} className="hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {dateRow.formatted_date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {dateRow.orders.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {dateRow.items_sold.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {dateRow.customers.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        {dateRow.tokens_collected.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300 text-center">
                        ${(dateRow.tokens_collected * tokenValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </>
  )
}

// Insights Tab Component
import type { EventInsightsStats } from '@/lib/api/types'

function InsightsTab({
  isLoading,
  data,
}: {
  isLoading: boolean
  data: EventInsightsStats | undefined
}) {
  const isVendor = usePermission('is_vendor')

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
              <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-20 mb-2" />
              <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-16" />
            </div>
          ))}
        </div>
        <div className="h-[300px] bg-neutral-100 dark:bg-neutral-900 rounded-lg animate-pulse" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
        No insights data available
      </div>
    )
  }

  // Helper to convert 24h to 12h format
  const formatHour12 = (hour24: string | number) => {
    const hour = typeof hour24 === 'string' ? parseInt(hour24.split(':')[0]) : hour24
    if (hour === 0) return '12 AM'
    if (hour === 12) return '12 PM'
    if (hour < 12) return `${hour} AM`
    return `${hour - 12} PM`
  }

  // Prepare chart data with 12h format
  const salesByHourData = (data.sales_by_hour || []).map(item => ({
    ...item,
    hour_label_12h: formatHour12(item.hour)
  }))
  
  const customerDistributionData = [
    { name: '1 Vendor', value: data.customer_distribution?.['1_vendor'] || 0 },
    { name: '2-3 Vendors', value: data.customer_distribution?.['2_3_vendors'] || 0 },
    { name: '4-5 Vendors', value: data.customer_distribution?.['4_5_vendors'] || 0 },
    { name: '6+ Vendors', value: data.customer_distribution?.['6_plus_vendors'] || 0 },
  ].filter(d => d.value > 0)

  const repeatCustomersData = [
    { name: 'One-time', value: data.repeat_customers?.one_time || 0 },
    { name: 'Repeat', value: data.repeat_customers?.repeat || 0 },
    { name: 'Frequent', value: data.repeat_customers?.frequent || 0 },
  ].filter(d => d.value > 0)

  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Key Metrics</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<Package className="w-5 h-5 text-blue-500" />}
            iconBg="bg-blue-50 dark:bg-blue-900/20"
            value={data.totals?.total_orders || 0}
            label="Total Orders"
          />
          <StatCard
            icon={<ShoppingCart className="w-5 h-5 text-green-500" />}
            iconBg="bg-green-50 dark:bg-green-900/20"
            value={data.totals?.total_items || 0}
            label="Total Items"
          />
          <StatCard
            icon={<Users className="w-5 h-5 text-yellow-500" />}
            iconBg="bg-yellow-50 dark:bg-yellow-900/20"
            value={data.totals?.total_customers || 0}
            label="Customers"
          />
          <StatCard
            icon={<DollarSign className="w-5 h-5 text-purple-500" />}
            iconBg="bg-purple-50 dark:bg-purple-900/20"
            value={formatCurrency(data.totals?.total_revenue || 0)}
            label="Total Revenue"
          />
        </div>

      {/* Averages */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <StatCard
            icon={<Activity className="w-5 h-5 text-cyan-500" />}
            iconBg="bg-cyan-50 dark:bg-cyan-900/20"
            value={`${(data.averages?.avg_order_value_tokens || 0).toFixed(1)} Tokens`}
            label="Avg. Order Value"
          />
          <StatCard
            icon={<TrendingUp className="w-5 h-5 text-orange-500" />}
            iconBg="bg-orange-50 dark:bg-orange-900/20"
            value={(data.averages?.avg_items_per_order || 0).toFixed(2)}
            label="Items/Order"
          />
          {!isVendor && (
            <StatCard
              icon={<Store className="w-5 h-5 text-pink-500" />}
              iconBg="bg-pink-50 dark:bg-pink-900/20"
              value={(data.averages?.avg_vendors_per_customer || 0).toFixed(2)}
              label="Vendors/User"
            />
          )}
          {!isVendor && (
            <StatCard
              icon={<Wallet className="w-5 h-5 text-indigo-500" />}
              iconBg="bg-indigo-50 dark:bg-indigo-900/20"
              value={`${(data.avg_reload_amount?.avg_tokens_per_customer || 0).toFixed(1)} Tokens`}
              label="Reload/User"
            />
          )}
        </div>
      </div>

      {/* Sales by Hour Chart */}
      {salesByHourData.length > 0 && (
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Sales by Hour</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Order volume throughout the day</p>
            </div>
            {data.top_hour && (
              <div className="text-right">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Peak Hour</p>
                <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{formatHour12(data.top_hour.hour)}</p>
                <p className="text-xs text-neutral-500">{data.top_hour.order_count} orders</p>
              </div>
            )}
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesByHourData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-800" />
                <XAxis 
                  dataKey="hour_label_12h" 
                  tick={{ fontSize: 11 }}
                  className="text-neutral-500 dark:text-neutral-400"
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  className="text-neutral-500 dark:text-neutral-400"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--tooltip-bg, white)',
                    border: '1px solid var(--tooltip-border, #e5e5e5)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value, name) => {
                    if (name === 'order_count') return [value, 'Orders']
                    if (name === 'items_sold') return [value, 'Items']
                    if (name === 'cash_revenue') return [formatCurrency(value as number), 'Revenue']
                    return [value, name]
                  }}
                />
                <Bar dataKey="order_count" fill="#3b82f6" name="order_count" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Customer Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendor Distribution */}
        {customerDistributionData.length > 0 && (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Customer Location Distribution</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">How many Locations each customer visited</p>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={customerDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {customerDistributionData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Customers']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Repeat Customers */}
        {repeatCustomersData.length > 0 && (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Customer Loyalty</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">One-time vs repeat customers</p>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={repeatCustomersData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {repeatCustomersData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Customers']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Reload Stats */}
      {data.avg_reload_amount && (
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Reload Statistics</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {data.avg_reload_amount.total_reloads?.toLocaleString() || 0}
              </p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">Total Reloads</p>
            </div>
            <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {data.avg_reload_amount.unique_customers?.toLocaleString() || 0}
              </p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">Unique Customers</p>
            </div>
            <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {data.avg_reload_amount.total_tokens_loaded?.toLocaleString() || 0}
              </p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">Tokens Sold</p>
            </div>
            <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(data.avg_reload_amount.total_cash_loaded || 0)}
              </p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">Pre-Tax Revenue</p>
            </div>
          </div>
        </div>
      )}

      {/* Top Customers Table */}
      {data.top_customers && data.top_customers.length > 0 && (
        <div className="-mx-4 sm:mx-0">
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 sm:rounded-lg overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-yellow-500" />
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Top Customers</h2>
              </div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Customers with highest spending</p>
            </div>
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-50 dark:bg-neutral-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Customer</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Orders</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Items</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Tokens Spent</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Cash Spent</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Vendors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {data.top_customers.map((customer, index) => (
                  <tr key={customer.user_id || index} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{customer.name || 'Unknown'}</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">{customer.email || customer.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-neutral-700 dark:text-neutral-300">
                      {customer.total_orders}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-neutral-700 dark:text-neutral-300">
                      {customer.total_items}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {customer.tokens_spent}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(customer.cash_spent)}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-neutral-700 dark:text-neutral-300">
                      {customer.vendors_visited}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* Location Summary Table */}
      {data.location_summary && data.location_summary.length > 0 && (
        <div className="-mx-4 sm:mx-0">
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 sm:rounded-lg overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-500" />
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Location Performance</h2>
              </div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Performance metrics by vendor location</p>
            </div>
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-50 dark:bg-neutral-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Booth</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Orders</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Items</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Orders/Hour</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Avg. Tokens</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Avg. Order</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase">Peak Hour</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {data.location_summary.map((location, index) => (
                  <tr key={location.vendor_event_id || index} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                    <td className="px-6 py-4 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {location.vendor_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                      {location.booth || '—'}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-neutral-700 dark:text-neutral-300">
                      {location.total_orders?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-neutral-700 dark:text-neutral-300">
                      {location.total_items?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-blue-600 dark:text-blue-400">
                      {location.orders_per_hour?.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {location.avg_tokens_per_order?.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(location.avg_cash_per_order || 0)}
                    </td>
                    <td className="px-1 sm:px-6 py-4 text-center">
                      {location.peak_hour ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md sm:rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                          {formatHour12(location.peak_hour.hour)} ({location.peak_hour.orders} orders)
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Review helper components
function ReviewRatingDisplay({ scaleSum, scaleCount, thumbsUp, thumbsDown }: { scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number }) {
  if (scaleCount > 0) {
    return (
      <div className="flex items-center justify-center gap-1">
        <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{(scaleSum / scaleCount).toFixed(1)}</span>
      </div>
    )
  }
  if (thumbsUp + thumbsDown > 0) {
    return (
      <div className="flex items-center justify-center gap-1">
        <ThumbsUp className="h-3.5 w-3.5 text-green-500 fill-green-500" />
        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{((thumbsUp / (thumbsUp + thumbsDown)) * 100).toFixed(0)}%</span>
      </div>
    )
  }
  return <span className="text-neutral-400">—</span>
}

function OrderReviewSummaryRating({ data }: { data?: OrderReviewSummary }) {
  if (!data || data.total_reviews === 0) return <span className="text-neutral-400">—</span>
  if (data.scale_avg > 0) {
    return (
      <div className="flex items-center justify-center gap-1">
        <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{data.scale_avg.toFixed(1)}</span>
      </div>
    )
  }
  if ((data.thumbs_up_count || 0) + (data.thumbs_down_count || 0) > 0) {
    return (
      <div className="flex items-center justify-center gap-1">
        <ThumbsUp className="h-3.5 w-3.5 text-green-500 fill-green-500" />
        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{(data.thumbs_up_percentage || 0).toFixed(0)}%</span>
      </div>
    )
  }
  return <span className="text-neutral-400">—</span>
}


function getRatingSortValue(data?: OrderReviewSummary): number {
  if (!data || data.total_reviews === 0) return -1
  if (data.scale_avg > 0) return data.scale_avg
  const total = (data.thumbs_up_count || 0) + (data.thumbs_down_count || 0)
  if (total > 0) return (data.thumbs_up_percentage || 0)
  return -1
}

function getItemRatingSortValue(item: { scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number }): number {
  if (item.scaleCount > 0) return item.scaleSum / item.scaleCount
  if (item.thumbsUp + item.thumbsDown > 0) return (item.thumbsUp / (item.thumbsUp + item.thumbsDown)) * 100
  return -1
}

function SortableHeader({ label, field, currentField, ascending, onSort, align = 'left' }: {
  label: string; field: string; currentField: string; ascending: boolean; onSort: (field: string) => void; align?: 'left' | 'center'
}) {
  return (
    <th
      className={cn(
        'px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none',
        align === 'center' ? 'text-center' : 'text-left'
      )}
      onClick={() => onSort(field)}
    >
      <span className={cn('inline-flex items-center gap-1', align === 'center' && 'justify-center')}>
        {label}
        <SortIcon active={currentField === field} ascending={ascending} />
      </span>
    </th>
  )
}

// Reviews Tab Component
type OrderReviewSubTab = 'by_vendor' | 'by_location'

function ReviewsTab({ eventId }: { eventId: number }) {
  const [orderSubTab, setOrderSubTab] = useState<OrderReviewSubTab>('by_vendor')
  const [reviewPage, setReviewPage] = useState(1)
  const [reviewSearch, setReviewSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [vendorSortField, setVendorSortField] = useState('totalReviews')
  const [vendorSortAsc, setVendorSortAsc] = useState(false)
  const [locationSortField, setLocationSortField] = useState('totalReviews')
  const [locationSortAsc, setLocationSortAsc] = useState(false)
  const [itemSortField, setItemSortField] = useState('totalReviews')
  const [itemSortAsc, setItemSortAsc] = useState(false)
  const { openOrderModal } = useModal()
  const { data: reportingData } = useReporting(eventId)
  const { data: vendorEventsData } = useVendorEvents(eventId)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(reviewSearch), 400)
    return () => clearTimeout(timer)
  }, [reviewSearch])

  // Fetch summaries
  const { data: orderSummary, isLoading: orderSummaryLoading } = useOrderReviewSummary({ event: eventId })
  const { data: itemSummary, isLoading: itemSummaryLoading } = useItemReviewSummary({ event: eventId })

  // Fetch recent reviews
  const { data: recentReviews, isLoading: reviewsLoading } = useOrderReviews({
    event: eventId,
    ordering: '-created_at',
    page: reviewPage,
    page_size: 20,
    expand: 'order,user',
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  })

  // Fetch item reviews grouped by item
  const { data: allItemReviews } = useItemReviews({
    event: eventId,
    expand: 'vendor_event_item',
    page_size: 200,
  })

  // Build item-to-vendor/location lookup from reporting data
  const itemNameToVendor = useMemo(() => {
    const lookup = new Map<string, string>()
    for (const i of reportingData?.items || []) {
      const name = i.vendor_event_item__name || i.name || ''
      const vendor = i.vendor_event_item__vendor_event__vendor__name || i.vendor_name || ''
      if (name && vendor) lookup.set(name, vendor)
    }
    return lookup
  }, [reportingData?.items])

  // Build per-item aggregates
  const itemReviewAggregates = useMemo(() => {
    if (!allItemReviews?.results) return []
    const map = new Map<number, { itemId: number; itemName: string; vendorName: string; totalReviews: number; scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number }>()
    for (const review of allItemReviews.results) {
      const vei = review.vendor_event_item
      const itemId = typeof vei === 'object' ? vei.id : vei
      const itemName = typeof vei === 'object' ? vei.name : `Item #${itemId}`
      const vendorName = itemNameToVendor.get(itemName) ?? ''
      if (!map.has(itemId)) {
        map.set(itemId, { itemId, itemName, vendorName, totalReviews: 0, scaleSum: 0, scaleCount: 0, thumbsUp: 0, thumbsDown: 0 })
      }
      const entry = map.get(itemId)!
      entry.totalReviews++
      for (const cr of review.criterion_ratings || []) {
        if (cr.scale_rating != null) { entry.scaleSum += cr.scale_rating; entry.scaleCount++ }
        if (cr.thumbs_up != null) { if (cr.thumbs_up) entry.thumbsUp++; else entry.thumbsDown++ }
      }
    }
    return Array.from(map.values()).sort((a, b) => b.totalReviews - a.totalReviews)
  }, [allItemReviews?.results, itemNameToVendor])

  // Build vendor-level aggregates from reporting vendors data
  const vendorReviewAggregates = useMemo(() => {
    // Count locations per vendor from reporting data
    const vendorLocationCount = new Map<string, number>()
    for (const loc of reportingData?.locations || []) {
      const vn = loc.vendor__name || ''
      vendorLocationCount.set(vn, (vendorLocationCount.get(vn) || 0) + 1)
    }

    // Use reporting vendors as the source — they have vendor IDs and order counts
    const reportingVendors = (reportingData?.vendors || []) as { id?: number; name?: string; orders_received?: number }[]
    return reportingVendors.map(v => ({
      vendorName: v.name || 'Unknown',
      vendorId: v.id || 0,
      locationCount: vendorLocationCount.get(v.name || '') || 0,
      orderReviewCount: 0, // Will be populated by per-row component
    }))
  }, [reportingData?.vendors, reportingData?.locations])

  // Build location-level aggregates from reporting locations data
  const locationReviewAggregates = useMemo(() => {
    const locations = reportingData?.locations || []
    const vendorEvents = vendorEventsData?.results || []
    // Build lookup from booth name to vendor_event id
    const boothToVendorEventId = new Map<string, number>()
    for (const ve of vendorEvents) {
      if (ve.booth) boothToVendorEventId.set(ve.booth, ve.id)
    }
    return locations.map(loc => ({
      locationName: loc.booth || loc.vendor__name || 'Unknown',
      vendorName: loc.vendor__name || 'Unknown',
      vendorEventId: boothToVendorEventId.get(loc.booth || '') || 0,
      orderReviewCount: 0, // Will be populated by per-row component
    }))
  }, [reportingData?.locations, vendorEventsData?.results])

  // Fetch all vendor order review summaries in parallel using useQueries
  const { token } = useAuth()
  const vendorSummaryQueries = useQueries({
    queries: vendorReviewAggregates.map(v => ({
      queryKey: queryKeys.orderReviewSummary({ vendor: v.vendorId, event: eventId }),
      queryFn: () => apiClient.getOrderReviewSummary(token!, { vendor: v.vendorId, event: eventId }),
      enabled: !!token,
    })),
  })
  const vendorReviewData = useMemo(() => {
    return vendorReviewAggregates.map((v, i) => {
      const data = vendorSummaryQueries[i]?.data as OrderReviewSummary | undefined
      return { ...v, totalReviews: data?.total_reviews || 0, ratingValue: getRatingSortValue(data), summaryData: data }
    })
  }, [vendorReviewAggregates, vendorSummaryQueries])

  // Fetch all location order review summaries in parallel
  const locationSummaryQueries = useQueries({
    queries: locationReviewAggregates.map(loc => ({
      queryKey: queryKeys.orderReviewSummary(loc.vendorEventId ? { vendor_event: loc.vendorEventId } : {}),
      queryFn: () => apiClient.getOrderReviewSummary(token!, loc.vendorEventId ? { vendor_event: loc.vendorEventId } : {}),
      enabled: !!token && loc.vendorEventId > 0,
    })),
  })
  const locationReviewData = useMemo(() => {
    return locationReviewAggregates.map((loc, i) => {
      const data = locationSummaryQueries[i]?.data as OrderReviewSummary | undefined
      return { ...loc, totalReviews: data?.total_reviews || 0, ratingValue: getRatingSortValue(data), summaryData: data }
    })
  }, [locationReviewAggregates, locationSummaryQueries])

  // Sort vendors
  const sortedVendors = useMemo(() => {
    return [...vendorReviewData].sort((a, b) => {
      const aVal = a[vendorSortField as keyof typeof a] ?? 0
      const bVal = b[vendorSortField as keyof typeof b] ?? 0
      if (typeof aVal === 'string') return vendorSortAsc ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal)
      return vendorSortAsc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal)
    })
  }, [vendorReviewData, vendorSortField, vendorSortAsc])

  // Sort locations
  const sortedLocations = useMemo(() => {
    return [...locationReviewData].sort((a, b) => {
      const aVal = a[locationSortField as keyof typeof a] ?? 0
      const bVal = b[locationSortField as keyof typeof b] ?? 0
      if (typeof aVal === 'string') return locationSortAsc ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal)
      return locationSortAsc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal)
    })
  }, [locationReviewData, locationSortField, locationSortAsc])

  // Sort items
  const sortedItems = useMemo(() => {
    return [...itemReviewAggregates].sort((a, b) => {
      if (itemSortField === 'ratingValue') {
        const aVal = getItemRatingSortValue(a)
        const bVal = getItemRatingSortValue(b)
        return itemSortAsc ? aVal - bVal : bVal - aVal
      }
      const aVal = a[itemSortField as keyof typeof a] ?? 0
      const bVal = b[itemSortField as keyof typeof b] ?? 0
      if (typeof aVal === 'string') return itemSortAsc ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal)
      return itemSortAsc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal)
    })
  }, [itemReviewAggregates, itemSortField, itemSortAsc])

  const handleVendorSort = (field: string) => {
    if (vendorSortField === field) setVendorSortAsc(!vendorSortAsc)
    else { setVendorSortField(field); setVendorSortAsc(false) }
  }
  const handleLocationSort = (field: string) => {
    if (locationSortField === field) setLocationSortAsc(!locationSortAsc)
    else { setLocationSortField(field); setLocationSortAsc(false) }
  }
  const handleItemSort = (field: string) => {
    if (itemSortField === field) setItemSortAsc(!itemSortAsc)
    else { setItemSortField(field); setItemSortAsc(false) }
  }

  const isLoading = orderSummaryLoading || itemSummaryLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
              <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-20 mb-2" />
              <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const totalOrderReviews = orderSummary?.total_reviews || 0
  const scaleAvg = orderSummary?.scale_avg || 0
  const thumbsUpPct = orderSummary?.thumbs_up_percentage || 0
  const totalItemReviews = itemSummary?.total_reviews || 0

  // Build chart data for criteria summary
  const criteriaChartData = (itemSummary?.criteria_summary || []).map(c => ({
    name: c.label,
    value: c.input_type === 'scale' ? (c.scale_avg || 0) : (c.thumbs_up_percentage || 0),
    type: c.input_type,
  }))

  // Thumbs distribution chart data
  const thumbsData = [
    { name: 'Positive', value: orderSummary?.thumbs_up_count || 0, fill: '#22c55e' },
    { name: 'Negative', value: orderSummary?.thumbs_down_count || 0, fill: '#ef4444' },
  ]

  const totalPages = recentReviews ? Math.ceil(recentReviews.count / 20) : 0

  return (
    <div className="space-y-6">
      {/* Summary Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          value={totalOrderReviews}
          label="Order Reviews"
        />
        <StatCard
          icon={<Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />}
          iconBg="bg-yellow-100 dark:bg-yellow-900/30"
          value={scaleAvg > 0 ? `${scaleAvg.toFixed(1)} / 5` : '—'}
          label="Avg Rating"
        />
        <StatCard
          icon={<ThumbsUp className="h-5 w-5 text-green-600 dark:text-green-400" />}
          iconBg="bg-green-100 dark:bg-green-900/30"
          value={thumbsUpPct > 0 ? `${thumbsUpPct.toFixed(0)}%` : '—'}
          label="Positive"
        />
        <StatCard
          icon={<MessageSquare className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
          iconBg="bg-purple-100 dark:bg-purple-900/30"
          value={totalItemReviews}
          label="Item Reviews"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Thumbs Distribution */}
        {(orderSummary?.thumbs_up_count || 0) + (orderSummary?.thumbs_down_count || 0) > 0 && (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Thumbs Distribution</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={thumbsData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {thumbsData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend formatter={(value) => <span style={{ fontSize: '12px', fontWeight: 500 }}>{value}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Criteria Averages */}
        {criteriaChartData.length > 0 && (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Item Review Criteria</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={criteriaChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, _name, props) => {
                    const v = Number(value) || 0
                    const type = (props as { payload?: { type?: string } })?.payload?.type
                    return type === 'scale' ? `${v.toFixed(1)} / 5` : `${v.toFixed(0)}%`
                  }}
                />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Recent Reviews Table */}
      <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Recent Order Reviews</h3>
            <input
              type="text"
              placeholder="Search reviews..."
              value={reviewSearch}
              onChange={(e) => { setReviewSearch(e.target.value); setReviewPage(1) }}
              className="w-full sm:w-64 px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {reviewsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : !recentReviews?.results?.length ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400 text-sm">
            No reviews found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Comment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  {recentReviews.results.map((review: OrderReview) => {
                    const orderObj = typeof review.order === 'object' ? review.order : null
                    const userObj = typeof review.user === 'object' ? review.user as User : null
                    const orderId = typeof review.order === 'number' ? review.order : review.order?.id

                    return (
                      <tr key={review.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900/30">
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => openOrderModal(orderId, eventId)}
                            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                          >
                            #{orderObj?.order_display_id || orderId}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                          {userObj ? `${userObj.first_name} ${userObj.last_name}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {review.rating_type === 'scale' && review.scale_rating != null ? (
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star
                                  key={s}
                                  className={cn(
                                    'h-3.5 w-3.5',
                                    s <= review.scale_rating!
                                      ? 'text-yellow-400 fill-yellow-400'
                                      : 'text-neutral-300 dark:text-neutral-600'
                                  )}
                                />
                              ))}
                            </div>
                          ) : review.rating_type === 'thumbs' ? (
                            review.thumbs_up ? (
                              <ThumbsUp className="h-4 w-4 text-green-600 dark:text-green-500 fill-green-600 dark:fill-green-500" />
                            ) : (
                              <ThumbsDown className="h-4 w-4 text-red-600 dark:text-red-500 fill-red-600 dark:fill-red-500" />
                            )
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400 max-w-xs truncate">
                          {review.comment || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                          {format(new Date(review.created_at), 'MMM d, yyyy')}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200 dark:border-neutral-800">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {recentReviews.count} review{recentReviews.count !== 1 ? 's' : ''}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReviewPage(p => Math.max(1, p - 1))}
                    disabled={reviewPage <= 1}
                    className="px-3 py-1 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md disabled:opacity-50 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-neutral-600 dark:text-neutral-400">
                    {reviewPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setReviewPage(p => Math.min(totalPages, p + 1))}
                    disabled={reviewPage >= totalPages}
                    className="px-3 py-1 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md disabled:opacity-50 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Order Reviews — By Vendor / By Location */}
      <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">Order Reviews</h3>
          <div className="flex gap-4">
            {([['by_vendor', 'By Vendor'], ['by_location', 'By Location']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setOrderSubTab(key)}
                className={cn(
                  'pb-1 text-sm font-medium border-b-2 transition-colors',
                  orderSubTab === key
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {orderSubTab === 'by_vendor' && (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                  <SortableHeader label="Vendor" field="vendorName" currentField={vendorSortField} ascending={vendorSortAsc} onSort={handleVendorSort} />
                  <SortableHeader label="Locations" field="locationCount" currentField={vendorSortField} ascending={vendorSortAsc} onSort={handleVendorSort} align="center" />
                  <SortableHeader label="Reviews" field="totalReviews" currentField={vendorSortField} ascending={vendorSortAsc} onSort={handleVendorSort} align="center" />
                  <SortableHeader label="Rating" field="ratingValue" currentField={vendorSortField} ascending={vendorSortAsc} onSort={handleVendorSort} align="center" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {sortedVendors.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">No order reviews yet</td></tr>
                ) : sortedVendors.map((v) => (
                  <tr key={v.vendorName} className="hover:bg-neutral-50 dark:hover:bg-neutral-900/30">
                    <td className="px-4 py-3 text-sm font-medium text-neutral-900 dark:text-neutral-100">{v.vendorName}</td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-600 dark:text-neutral-400">{v.locationCount}</td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-600 dark:text-neutral-400">{v.totalReviews}</td>
                    <td className="px-4 py-3 text-center"><OrderReviewSummaryRating data={v.summaryData} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {orderSubTab === 'by_location' && (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                  <SortableHeader label="Location" field="locationName" currentField={locationSortField} ascending={locationSortAsc} onSort={handleLocationSort} />
                  <SortableHeader label="Vendor" field="vendorName" currentField={locationSortField} ascending={locationSortAsc} onSort={handleLocationSort} />
                  <SortableHeader label="Reviews" field="totalReviews" currentField={locationSortField} ascending={locationSortAsc} onSort={handleLocationSort} align="center" />
                  <SortableHeader label="Rating" field="ratingValue" currentField={locationSortField} ascending={locationSortAsc} onSort={handleLocationSort} align="center" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {sortedLocations.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">No order reviews yet</td></tr>
                ) : sortedLocations.map((loc) => (
                  <tr key={`${loc.vendorName}-${loc.locationName}`} className="hover:bg-neutral-50 dark:hover:bg-neutral-900/30">
                    <td className="px-4 py-3 text-sm font-medium text-neutral-900 dark:text-neutral-100">{loc.locationName}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400">{loc.vendorName}</td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-600 dark:text-neutral-400">{loc.totalReviews}</td>
                    <td className="px-4 py-3 text-center"><OrderReviewSummaryRating data={loc.summaryData} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Item Reviews — By Items */}
      <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Item Reviews</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                <SortableHeader label="Item" field="itemName" currentField={itemSortField} ascending={itemSortAsc} onSort={handleItemSort} />
                <SortableHeader label="Vendor" field="vendorName" currentField={itemSortField} ascending={itemSortAsc} onSort={handleItemSort} />
                <SortableHeader label="Reviews" field="totalReviews" currentField={itemSortField} ascending={itemSortAsc} onSort={handleItemSort} align="center" />
                <SortableHeader label="Rating" field="ratingValue" currentField={itemSortField} ascending={itemSortAsc} onSort={handleItemSort} align="center" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">No item reviews yet</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={item.itemId} className="hover:bg-neutral-50 dark:hover:bg-neutral-900/30">
                  <td className="px-4 py-3 text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.itemName}</td>
                  <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400">{item.vendorName || '—'}</td>
                  <td className="px-4 py-3 text-center text-sm text-neutral-600 dark:text-neutral-400">{item.totalReviews}</td>
                  <td className="px-4 py-3 text-center">
                    <ReviewRatingDisplay scaleSum={item.scaleSum} scaleCount={item.scaleCount} thumbsUp={item.thumbsUp} thumbsDown={item.thumbsDown} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
